import React, { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { Button, Header, Icon, Modal, ModalActions, ModalContent } from 'semantic-ui-react';
import LuxonDataTable from '../../../components/LuxonDataTable';
import Prospect from '../../../types/entities/Prospect';
import { createOrderFromProspectAppointment, getCompletedProspectsForOrder } from '../../../services/prospects.service';
import InputSearch from '../../../components/InputSearch';
import { formatDateTime } from '../../../utils/datetimeUtils';
import CreateOrderFromScratchModal from './CreateOrderFromScratchModal';
import { toast } from 'react-toastify';
import { useAppContext } from '../../../hooks/AppProvider';
import ProspectAppointment from '../../../types/entities/ProspectAppointment';

const CreateOrderFromProspectModal = () => {

    const { updateParentState } = useAppContext();

    const [loading, setLoading] = useState(false);
    const [openModal, setOpenModal] = useState(false);

    const [perPage, setPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [, setCurrentPage] = useState(1);
    const [data, setData] = useState<Prospect[]>([]);

    const [search, setSearch] = useState("");

    const columns = [
        {
            name: "Nombre",
            selector: (row: ProspectAppointment) => row.prospect.person?.fullName,
        },
        {
            name: "Fecha de visita",
            selector: (row: ProspectAppointment) => formatDateTime(row.appointment.dateTime),
        },
        {
            name: "Producto de interes",
            selector: (row: ProspectAppointment) => `${row.prospect.product?.name} - ${row.prospect.product?.name}`,
        },
        {
            name: "Crear pedido",
            button: true,
            style: { justifyContent: "right", paddingRight: "16px", paddingLeft: "16px" },
            cell: (row: ProspectAppointment) => (
                <>
                    <Button color='green' icon size="mini" onClick={(event) => handleCreateFromProspect(event, row.id)}>
                        <Icon name="plus circle" />
                    </Button>
                </>
            )
        },
    ];

    const fetchData = useCallback(async (page: number, rowsPerPage: number, search: string) => {
        setLoading(true);
        try {
            const response = await getCompletedProspectsForOrder(rowsPerPage, page - 1, "createdAt,desc", search);
            setData(response?.data.content);
            setTotalRows(response?.data.totalElements);
        } catch (error) {
            toast.error("Error al obtener el listado de clientes");
        } finally {
            setLoading(false);
        }
    }, []);

    // Handlers that call the fetchData method
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        fetchData(page, perPage, search);
    };

    const handlePerRowsChange = (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
        fetchData(page, newPerPage, search);
    };

    useEffect(() => {
        fetchData(1, perPage, search);
        // eslint-disable-next-line
    }, [search]);

    useEffect(() => {
        if (openModal) {
            fetchData(1, perPage, search);
        }
    }, [openModal, perPage, search, fetchData]);

    const handleCreateFromProspect = async (event: SyntheticEvent, prospectAppointmentId: string) => {
        event.preventDefault();
        updateParentState({ loading: true });
        try {
            if (!prospectAppointmentId) {
                throw new Error("No se ha seleccionado un prospecto");
            }
            const response = await createOrderFromProspectAppointment(prospectAppointmentId);
            if (response?.status === 201) {
                window.location.href = `/orders/${response.data.id}/form`;
            } else {
                throw new Error("Ha ocurrido un error al crear el pedido desde el prospecto seleccionado.");
            }
        } catch (error: any) {
            toast.error(error.message);
        } finally {
            updateParentState({ loading: false });
        }
        setOpenModal(false);
    };

    const handleOpenModal = (event: SyntheticEvent) => {
        event.preventDefault();
        setOpenModal(true);
    };

    const handleCancelModal = (event: SyntheticEvent) => {
        event.preventDefault();
        setOpenModal(false);
    };

    return (
        <>
            <Modal
                centered={false}
                closeOnDimmerClick
                closeIcon={true}
                size="large"
                onClose={(event) => handleCancelModal(event)}
                open={openModal}
                trigger={
                    <Button floated='right' onClick={(event) => handleOpenModal(event)}>
                        Crear nuevo pedido
                    </Button>
                } >
                <Header icon="plus circle" content="Crear desde venta concretada (prospectos)" />
                <ModalContent>
                    <>
                        <div className="ui form">
                            <InputSearch
                                icon="search"
                                placeholder="Búsqueda por nombre o telefono..."
                                setSearch={setSearch}
                            />
                        </div>

                        <LuxonDataTable
                            columns={columns}
                            data={data}
                            loading={loading}
                            totalRows={totalRows}
                            handlePageChange={handlePageChange}
                            handlePerRowsChange={handlePerRowsChange}
                        />
                    </>
                </ModalContent>
                <ModalActions>
                    <Button onClick={(event) => handleCancelModal(event)} >
                        <Icon name="cancel" /> Cancelar
                    </Button>
                    <CreateOrderFromScratchModal />
                </ModalActions>
            </Modal>
        </>
    );
};

export default CreateOrderFromProspectModal;
